import { useEffect, useRef } from 'react';
import {
  createChatbotWebChatMiddleware,
  createStartConversationActivity,
} from '../transport/webChatMiddleware';

export default function useWebChatStore({
  code,
  createStore,
  features,
  freeze = false,
  isMobile,
  ...params
}) {
  const freezeRef = useRef(freeze);

  useEffect(() => {
    freezeRef.current = freeze;
  }, [freeze]);

  // Kept live because the store below is created once, so the middleware would
  // otherwise close over the first render's values, both empty before the token
  // fetch resolves. Two refs, not one: the middleware clears codeRef after
  // sending (the code is single-use), and a combined object would resurrect it
  // the next time features changed.
  const codeRef = useRef(code);
  const featuresRef = useRef(features);
  const hasConnectedRef = useRef(false);

  useEffect(() => {
    codeRef.current = code;
  }, [code]);

  useEffect(() => {
    featuresRef.current = features;
  }, [features]);

  const storeRef = useRef();
  if (!storeRef.current) {
    storeRef.current = createStore(
      {},
      createChatbotWebChatMiddleware({
        ...params,
        freezeRef,
        hasConnectedRef,
        isMobile,
        codeRef,
        featuresRef,
      }),
    );
  }

  useEffect(() => {
    if (!code || !hasConnectedRef.current) {
      return;
    }

    // features comes from the ref, not the dependency list: re-dispatching
    // startConversation restarts the bot's flow, so only a new code re-sends,
    // carrying whatever features resolved with it.
    storeRef.current.dispatch(
      createStartConversationActivity({
        code,
        features: featuresRef.current,
        isMobile,
      }),
    );
  }, [code, isMobile]);

  return storeRef.current;
}
